import React, { useState, useEffect, useCallback} from "react";
import "./App.scss";
import Header from "./components/Header";
import Footer from "./components/Footer";
import About from "./components/About";
import Experience from "./components/Experience";
import Projects from "./components/Projects";
import Skills from "./components/Skills";
import BlogShowcase from "./components/Blogs";

const App = () => {
  const [resumeData, setResumeData] = useState({});
  const [sharedData, setSharedData] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch resume JSON data using native fetch API
  const loadResumeFromPath = async (path) => {
    try {
      const response = await fetch(path, { cache: "no-store" });
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();
      setResumeData(data);
    } catch (err) {
      alert(err);
    }
  };

  // Fetch shared data
  const loadSharedData = async () => {
    try {
      const response = await fetch("portfolio_shared_data.json", {
        cache: "no-store",
      });
      if (!response.ok) throw new Error(response.statusText);
      const data = await response.json();
      setSharedData(data);
      if (data?.basic_info?.name) {
        document.title = data.basic_info.name;
      }
    } catch (err) {
      alert(err);
    }
  };

  // Swap the visual cue of the language icons to indicate selection
  const swapCurrentlyActiveLanguage = (oppositeLangIconId) => {
    const pickedLangIconId =
      oppositeLangIconId === window.$primaryLanguageIconId
        ? window.$secondaryLanguageIconId
        : window.$primaryLanguageIconId;

    const oppositeElement = document.getElementById(oppositeLangIconId);
    const pickedElement = document.getElementById(pickedLangIconId);

    if (oppositeElement) {
      oppositeElement.removeAttribute("filter");
    } else {
      console.warn(`Element with id ${oppositeLangIconId} not found.`);
    }

    if (pickedElement) {
      pickedElement.setAttribute("filter", "brightness(40%)");
    } else {
      console.warn(`Element with id ${pickedLangIconId} not found.`);
    }
  };

  // Apply the chosen language by swapping the icon and loading the proper resume file
  const applyPickedLanguage = useCallback(async (pickedLanguage, oppositeLangIconId) => {
    if (!pickedLanguage || !oppositeLangIconId) {
      console.warn("Invalid arguments passed to applyPickedLanguage.");
      return;
    }

    swapCurrentlyActiveLanguage(oppositeLangIconId);
    document.documentElement.lang = pickedLanguage;

    const resumePath =
      pickedLanguage === window.$primaryLanguage
        ? "english.json"
        : `${pickedLanguage}.json`;

    try {
      await loadResumeFromPath(resumePath);
    } catch (error) {
      console.error("Error loading resume data:", error);
    }
  }, []);


  // Load all data on mount
  useEffect(() => {
    const initializeData = async () => {
      await loadSharedData();
      await applyPickedLanguage(
        window.$primaryLanguage,
        window.$secondaryLanguageIconId
      );
      setLoading(false);
    };
    initializeData();
  }, [applyPickedLanguage]);
    

  // Style object for language flag icons
  const flagStyle = { margin: "0 10px", cursor: "pointer" };

  if (loading) {
    return (
      <div className="loading-container" style={{ textAlign: "center", padding: "50px" }}>
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div>
      <Header sharedData={sharedData.basic_info} />
      <div className="col-md-12 mx-auto text-center language">
        <p>SWITCH LANGUAGE</p>
        <div
          onClick={() =>
            applyPickedLanguage("english", window.$secondaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon mr-5"
            data-icon="twemoji-flag-for-flag-united-kingdom"
            data-inline="false"
            id={window.$primaryLanguageIconId}
            style={flagStyle}
          ></span>
        </div>
        <div
          onClick={() =>
            applyPickedLanguage("swahili", window.$primaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon"
            data-icon="twemoji-flag-for-flag-kenya"
            data-inline="false"
            style={flagStyle}
          ></span>
        </div>
        <div
          onClick={() =>
            applyPickedLanguage("igbo", window.$primaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon"
            data-icon="twemoji-flag-for-flag-nigeria"
            data-inline="false"
            style={flagStyle}
          ></span>
        </div>
        <div
          onClick={() =>
            applyPickedLanguage("zulu", window.$primaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon"
            data-icon="twemoji-flag-for-flag-south-africa"
            data-inline="false"
            style={flagStyle}
          ></span>
        </div>
        <div
          onClick={() =>
            applyPickedLanguage("german", window.$primaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon"
            data-icon="twemoji-flag-for-flag-germany"
            data-inline="false"
            style={flagStyle}
          ></span>
        </div>
        <div
          onClick={() =>
            applyPickedLanguage("spanish", window.$primaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon"
            data-icon="twemoji-flag-for-flag-spain"
            data-inline="false"
            style={flagStyle}
          ></span>
        </div>
        <div
          onClick={() =>
            applyPickedLanguage("chinese", window.$primaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon"
            data-icon="twemoji-flag-for-flag-china"
            data-inline="false"
            style={flagStyle}
          ></span>
        </div>
        <div
          onClick={() =>
            applyPickedLanguage("french", window.$primaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon"
            data-icon="twemoji-flag-for-flag-france"
            data-inline="false"
            style={flagStyle}
          ></span>
        </div>
        <div
          onClick={() =>
            applyPickedLanguage("arabic", window.$primaryLanguageIconId)
          }
          style={{ display: "inline" }}
        >
          <span
            className="iconify language-icon"
            data-icon="twemoji-flag-for-flag-saudi-arabia"
            data-inline="false"
            style={flagStyle}
          ></span>
        </div>
      </div>
      <About
        resumeBasicInfo={resumeData.basic_info}
        sharedBasicInfo={sharedData.basic_info}
      />
      <Projects
        resumeProjects={resumeData.projects}
        resumeBasicInfo={resumeData.basic_info}
      />
      <Skills
        sharedSkills={sharedData.skills}
        resumeBasicInfo={resumeData.basic_info}
      />
      <BlogShowcase />
      <Experience
        resumeExperience={resumeData.experience}
        resumeBasicInfo={resumeData.basic_info}
      />
      <Footer sharedBasicInfo={sharedData.basic_info} />
    </div>
  );
};

export default App;
